import { useState, useEffect } from "react";
import { useTranslations } from "use-intl";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Users, Loader2, Plus, Pencil, Trash2, Merge } from "lucide-react";
import { ServiceRegistry } from "@/services/ServiceRegistry";
import { Employee } from "@/types/employee";
import { toast } from "sonner";
import { ibanError } from "./employeeIbanValidation";
import { userFacingErrorMessage } from '@/utils/userFacingError';

// An example IBAN, the same in every language.
const EXAMPLE_IBAN = "SE45 5000 0000 0000 1234";

function maskIban(iban: string | null): string {
  if (!iban) return "—";
  const cleaned = iban.replace(/\s/g, "");
  if (cleaned.length <= 8) return cleaned;
  const first4 = cleaned.slice(0, 4);
  const last4 = cleaned.slice(-4);
  return `${first4} **** ${last4}`;
}

export const EmployeeSettings = () => {
  const t = useTranslations("consultantSettings.employees");
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState<Employee | null>(null);
  const [name, setName] = useState("");
  const [iban, setIban] = useState("");
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const [mergeSource, setMergeSource] = useState<Employee | null>(null);
  const [mergeTargetId, setMergeTargetId] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<Employee | null>(null);

  const employeeService = ServiceRegistry.getEmployeeService();

  useEffect(() => {
    loadEmployees();
  }, []);

  const loadEmployees = async () => {
    try {
      setLoading(true);
      const result = await employeeService.listEmployees();
      setEmployees(result);
    } catch (error) {
      console.error("Error loading employees:", error);
      toast.error(t("loadFailed"));
    } finally {
      setLoading(false);
    }
  };

  const openAddDialog = () => {
    setEditingEmployee(null);
    setName("");
    setIban("");
    setDialogOpen(true);
  };

  const openEditDialog = (employee: Employee) => {
    setEditingEmployee(employee);
    setName(employee.name ?? "");
    setIban(employee.iban ?? "");
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      // Validate IBAN if provided
      const trimmedIban = iban.trim();
      if (trimmedIban) {
        const invalidIban = ibanError(trimmedIban);
        if (invalidIban) {
          toast.error(t(invalidIban === "length" ? "ibanLength" : "ibanFormat"));
          return;
        }
      }

      if (editingEmployee) {
        const updateData: { name?: string | null; iban?: string | null } = {
          iban: trimmedIban || null,
        };
        // Only allow name changes for employees without a user account
        if (!editingEmployee.user_id) {
          updateData.name = name.trim() || null;
        }
        await employeeService.updateEmployee(editingEmployee.id, updateData);
        toast.success(t("updated"));
      } else {
        if (!name.trim()) {
          toast.error(t("nameRequired"));
          return;
        }
        await employeeService.createEmployee({
          name: name.trim(),
          iban: trimmedIban || undefined,
        });
        toast.success(t("added"));
      }
      setDialogOpen(false);
      await loadEmployees();
    } catch (error) {
      console.error("Error saving employee:", error);
      toast.error(
        editingEmployee ? t("updateFailed") : t("addFailed")
      );
    }
  };

  const handleDeleteConfirmed = async () => {
    if (!deleteTarget) return;
    const employee = deleteTarget;
    setDeleteTarget(null);
    try {
      await employeeService.deleteEmployee(employee.id);
      toast.success(t("deleted"));
      await loadEmployees();
    } catch (error: unknown) {
      console.error("Error deleting employee:", error);
      const message =
        userFacingErrorMessage(error, t("deleteFailed"));
      toast.error(message);
    }
  };

  const openMergeDialog = (employee: Employee) => {
    setMergeSource(employee);
    setMergeTargetId("");
    setMergeDialogOpen(true);
  };

  const handleMerge = async () => {
    if (!mergeSource || !mergeTargetId) return;
    try {
      await employeeService.mergeEmployee(mergeSource.id, mergeTargetId);
      toast.success(t("merged"));
      setMergeDialogOpen(false);
      await loadEmployees();
    } catch (error: unknown) {
      console.error("Error merging employees:", error);
      const message =
        userFacingErrorMessage(error, t("mergeFailed"));
      toast.error(message);
    }
  };

  const mergeTargets = employees.filter(
    (candidate) => mergeSource && candidate.id !== mergeSource.id
  );

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              {t("title")}
            </CardTitle>
            <CardDescription>
              {t("description")}
            </CardDescription>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm" onClick={openAddDialog}>
                <Plus className="h-4 w-4 mr-1" />
                {t("add")}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>
                  {editingEmployee ? t("edit") : t("add")}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="employee-name">{t("name")}</Label>
                  <Input
                    id="employee-name"
                    value={editingEmployee?.user_id ? editingEmployee.display_name : name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={t("namePlaceholder")}
                    disabled={!!editingEmployee?.user_id}
                  />
                  {editingEmployee?.user_id && (
                    <p className="text-xs text-muted-foreground">
                      {t("nameManaged")}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="employee-iban">{t("iban")}</Label>
                  <Input
                    id="employee-iban"
                    value={iban}
                    onChange={(e) => setIban(e.target.value)}
                    placeholder={EXAMPLE_IBAN}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                >
                  {t("cancel")}
                </Button>
                <Button onClick={handleSave}>
                  {editingEmployee ? t("saveChanges") : t("add")}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        {loading && employees.length === 0 ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : employees.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4">
            {t("empty")}
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("name")}</TableHead>
                <TableHead>{t("iban")}</TableHead>
                <TableHead className="w-[100px]">{t("actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {employees.map((employee) => (
                <TableRow key={employee.id}>
                  <TableCell className="font-medium">
                    {employee.display_name}
                  </TableCell>
                  <TableCell className="font-mono text-sm">
                    {maskIban(employee.iban)}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openEditDialog(employee)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      {!employee.user_id && (
                        <Button
                          variant="ghost"
                          size="icon"
                          title={t("merge")}
                          onClick={() => openMergeDialog(employee)}
                        >
                          <Merge className="h-4 w-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setDeleteTarget(employee)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>

      <Dialog open={mergeDialogOpen} onOpenChange={setMergeDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("mergeTitle")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-muted-foreground">
              {t.rich("mergeBody", {
                name: mergeSource?.display_name ?? "",
                employee: (chunks) => <span className="font-medium text-foreground">{chunks}</span>,
              })}
            </p>
            <div className="space-y-2">
              <Label>{t("mergeInto")}</Label>
              <Select value={mergeTargetId} onValueChange={setMergeTargetId}>
                <SelectTrigger>
                  <SelectValue placeholder={t("mergeTarget")} />
                </SelectTrigger>
                <SelectContent>
                  {mergeTargets.map((target) => (
                    <SelectItem key={target.id} value={target.id}>
                      {target.display_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMergeDialogOpen(false)}>
              {t("cancel")}
            </Button>
            <Button onClick={handleMerge} disabled={!mergeTargetId}>
              {t("mergeAction")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("deleteTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("deleteBody", { name: deleteTarget?.display_name ?? "" })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteConfirmed}>{t("delete")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};
